export function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
}

export function createCubicBezierEasingFunction(x1, y1, x2, y2) {
    const NEWTON_ITERATIONS = 4
    const NEWTON_MIN_SLOPE = 0.001
    const SUBDIVISION_PRECISION = 0.0000001
    const SUBDIVISION_MAX_ITERATIONS = 10
    const kSplineTableSize = 11
    const kSampleStepSize = 1 / (kSplineTableSize - 1)

    const A = (aA1, aA2) => 1 - 3 * aA2 + 3 * aA1
    const B = (aA1, aA2) => 3 * aA2 - 6 * aA1
    const C = (aA1) => 3 * aA1
    const calcBezier = (aT, aA1, aA2) => ((A(aA1, aA2) * aT + B(aA1, aA2)) * aT + C(aA1)) * aT
    const getSlope = (aT, aA1, aA2) => 3 * A(aA1, aA2) * aT * aT + 2 * B(aA1, aA2) * aT + C(aA1)

    const binarySubdivide = (aX, aA, aB) => {
        let currentX
        let currentT
        let i = 0
        do {
            currentT = aA + (aB - aA) / 2
            currentX = calcBezier(currentT, x1, x2) - aX
            if (currentX > 0) {
                aB = currentT
            } else {
                aA = currentT
            }
        } while (Math.abs(currentX) > SUBDIVISION_PRECISION && ++i < SUBDIVISION_MAX_ITERATIONS)
        return currentT
    }

    const newtonRaphsonIterate = (aX, aGuessT) => {
        let i = 0
        let currentT = aGuessT
        while (i < NEWTON_ITERATIONS) {
            const currentSlope = getSlope(currentT, x1, x2)
            if (currentSlope === 0) {
                return currentT
            }
            const currentX = calcBezier(currentT, x1, x2) - aX
            currentT -= currentX / currentSlope
            i += 1
        }
        return currentT
    }

    if (x1 === y1 && x2 === y2) {
        return (t) => t
    }

    const sampleValues = new Array(kSplineTableSize)
    for (let i = 0; i < kSplineTableSize; i += 1) {
        sampleValues[i] = calcBezier(i * kSampleStepSize, x1, x2)
    }

    const getTForX = (aX) => {
        let intervalStart = 0
        let currentSample = 1
        const lastSample = kSplineTableSize - 1

        while (currentSample !== lastSample && sampleValues[currentSample] <= aX) {
            intervalStart += kSampleStepSize
            currentSample += 1
        }
        currentSample -= 1

        const dist = (aX - sampleValues[currentSample]) / (sampleValues[currentSample + 1] - sampleValues[currentSample])
        const guessForT = intervalStart + dist * kSampleStepSize
        const initialSlope = getSlope(guessForT, x1, x2)

        if (initialSlope >= NEWTON_MIN_SLOPE) {
            return newtonRaphsonIterate(aX, guessForT)
        }
        if (initialSlope === 0) {
            return guessForT
        }
        return binarySubdivide(aX, intervalStart, intervalStart + kSampleStepSize)
    }

    return (t) => {
        if (t <= 0) return 0
        if (t >= 1) return 1
        return calcBezier(getTForX(t), y1, y2)
    }
}
